import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import { I18N_LANGUAGES } from '@/i18n';
import { useTranslateAddonMutation } from '@/services/AddonManagementService';
import { enqueueSnackbar } from 'notistack';
import { useIntl } from 'react-intl';
import { IAddonData } from '@/types/addon-management.types';

type AddonTranslationMap = NonNullable<IAddonData['translations']>;
type AddonTranslationRow = AddonTranslationMap[string];

interface AddonPreviewModalProps extends Omit<ModalProps, 'children'> {
  formData: Partial<IAddonData>;
  source_language?: string;
  initialTranslations?: IAddonData['translations'];
  onTranslationsUpdate?: (translations: AddonTranslationMap) => void;
}

const hasTranslations = (
  value?: IAddonData['translations']
): value is AddonTranslationMap =>
  Boolean(value && Object.keys(value).length > 0);

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return typeof error === 'string' ? error : undefined;
  }

  const maybeError = error as {
    data?: unknown;
    message?: unknown;
    error?: unknown;
    details?: unknown;
  };

  if (maybeError.data) {
    if (typeof maybeError.data === 'string') return maybeError.data;

    if (typeof maybeError.data === 'object') {
      const data = maybeError.data as {
        message?: unknown;
        error?: unknown;
        details?: unknown;
      };

      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
      if (typeof data.details === 'string') return data.details;
    }
  }

  if (typeof maybeError.message === 'string') return maybeError.message;
  if (typeof maybeError.error === 'string') return maybeError.error;
  if (typeof maybeError.details === 'string') return maybeError.details;

  return undefined;
};

const FIELD_LABELS = {
  en: {
    name: 'Name',
    description: 'Description',
    pickup: 'Pickup / usage instructions',
    return: 'Return instructions',
  },
  de: {
    name: 'Name',
    description: 'Beschreibung',
    pickup: 'Abholung / Nutzung',
    return: 'Rückgabe',
  },
  fi: {
    name: 'Nimi',
    description: 'Kuvaus',
    pickup: 'Nouto / käyttöohjeet',
    return: 'Palautusohjeet',
  },
  sv: {
    name: 'Namn',
    description: 'Beskrivning',
    pickup: 'Hämtning / användning',
    return: 'Retur',
  },
} as const;

function addonUsesSecretHandover(
  mode: string | null | undefined
): mode is 'KEY_BOX' | 'ACCESS_CODE' {
  return mode === 'KEY_BOX' || mode === 'ACCESS_CODE';
}

const LanguagePreview: React.FC<{
  language: { code: string; label: string; flag: string };
  formData: Partial<IAddonData>;
  translatedContent?: Record<string, AddonTranslationRow>;
  isEditable?: boolean;
  isLoading?: boolean;
  showKeyBox?: boolean;
  onEditContent?: (
    languageCode: string,
    field: keyof AddonTranslationRow,
    value: string
  ) => void;
}> = ({
  language,
  formData,
  translatedContent,
  isEditable = true,
  isLoading = false,
  showKeyBox = false,
  onEditContent,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<AddonTranslationRow>({});

  const fieldLabels =
    FIELD_LABELS[language.code as keyof typeof FIELD_LABELS] || FIELD_LABELS.en;

  const rowFromTranslations =
    translatedContent?.[language.code] ||
    formData.translations?.[language.code] ||
    {};
  const isSourceLang = (formData.language || 'en') === language.code;

  const langContent: Required<AddonTranslationRow> = {
    name:
      (isSourceLang ? (formData.name ?? '') : rowFromTranslations?.name) || '',
    description:
      (isSourceLang
        ? (formData.description ?? '')
        : rowFromTranslations?.description) || '',
    key_box_pickup_instructions:
      (isSourceLang
        ? (formData.key_box_pickup_instructions ?? '')
        : rowFromTranslations?.key_box_pickup_instructions) || '',
    key_box_return_instructions:
      (isSourceLang
        ? (formData.key_box_return_instructions ?? '')
        : rowFromTranslations?.key_box_return_instructions) || '',
  };

  const toggleEditMode = () => {
    if (!isEditable) return;
    if (!isEditing) {
      setEditValues({
        name: langContent.name,
        description: langContent.description,
        key_box_pickup_instructions: langContent.key_box_pickup_instructions,
        key_box_return_instructions: langContent.key_box_return_instructions,
      });
    }
    setIsEditing((v) => !v);
  };

  const handleSave = () => {
    if (onEditContent) {
      (Object.keys(editValues) as Array<keyof AddonTranslationRow>).forEach(
        (k) => {
          const v = editValues[k];
          if (v !== undefined) onEditContent(language.code, k, v);
        }
      );
    }
    setIsEditing(false);
  };

  const handleChange = (field: keyof AddonTranslationRow, value: string) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="bg-gray-100 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden mb-5 border border-gray-200">
      <div className="bg-gray-100 px-5 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center">
          <img
            src={language.flag}
            alt={`${language.label} flag`}
            className="w-6 h-6 mr-3 rounded-sm shadow-sm"
          />
          <h2 className="text-base font-medium text-gray-800 flex items-center">
            {language.label}
            {isLoading && (
              <span
                className="ml-2 flex items-center text-xs text-blue-600 font-medium"
                role="status"
                aria-live="polite"
              >
                <svg
                  className="animate-spin h-3 w-3 mr-1"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                translating...
              </span>
            )}
          </h2>
        </div>
        {!isLoading && isEditable && (
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="text-gray-600 hover:text-gray-800 p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-sm"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 flex items-center text-sm"
                  type="button"
                >
                  Save
                </button>
              </>
            ) : (
              <button
                onClick={toggleEditMode}
                className="bg-gray-100 text-blue-600 hover:text-blue-800 p-1.5 rounded-full hover:bg-blue-50 transition-colors"
                type="button"
                title="Edit translation"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {isLoading ? (
        <div
          className="p-5 flex justify-center"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <span className="sr-only">Loading translation</span>
          <div className="animate-pulse flex space-x-4 w-full">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded" />
                <div className="h-4 bg-gray-200 rounded w-5/6" />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-5">
          <div className="space-y-4">
            <div>
              <span className="block text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                {fieldLabels.name}
              </span>
              {isEditing ? (
                <input
                  id={`name-${language.code}`}
                  type="text"
                  value={editValues.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full p-2 bg-gray-100 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                />
              ) : (
                <p
                  className={clsx(
                    'text-base font-medium text-gray-800',
                    !langContent.name && 'text-gray-400 italic'
                  )}
                >
                  {langContent.name || '-'}
                </p>
              )}
            </div>

            <div>
              <span className="block text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                {fieldLabels.description}
              </span>
              {isEditing ? (
                <textarea
                  id={`description-${language.code}`}
                  value={editValues.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="w-full p-2 bg-gray-100 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base min-h-[100px]"
                />
              ) : (
                <p
                  className={clsx(
                    'text-base text-gray-700 whitespace-pre-wrap leading-relaxed',
                    !langContent.description && 'text-gray-400 italic'
                  )}
                >
                  {langContent.description || '-'}
                </p>
              )}
            </div>

            {showKeyBox && (
              <>
                <div>
                  <span className="block text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {fieldLabels.pickup}
                  </span>
                  {isEditing ? (
                    <textarea
                      id={`pickup-${language.code}`}
                      value={editValues.key_box_pickup_instructions || ''}
                      onChange={(e) =>
                        handleChange(
                          'key_box_pickup_instructions',
                          e.target.value
                        )
                      }
                      className="w-full p-2 bg-gray-100 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base min-h-[80px]"
                    />
                  ) : (
                    <p
                      className={clsx(
                        'text-base text-gray-700 whitespace-pre-wrap leading-relaxed',
                        !langContent.key_box_pickup_instructions &&
                          'text-gray-400 italic'
                      )}
                    >
                      {langContent.key_box_pickup_instructions || '-'}
                    </p>
                  )}
                </div>
                <div>
                  <span className="block text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {fieldLabels.return}
                  </span>
                  {isEditing ? (
                    <textarea
                      id={`return-${language.code}`}
                      value={editValues.key_box_return_instructions || ''}
                      onChange={(e) =>
                        handleChange(
                          'key_box_return_instructions',
                          e.target.value
                        )
                      }
                      className="w-full p-2 bg-gray-100 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base min-h-[80px]"
                    />
                  ) : (
                    <p
                      className={clsx(
                        'text-base text-gray-700 whitespace-pre-wrap leading-relaxed',
                        !langContent.key_box_return_instructions &&
                          'text-gray-400 italic'
                      )}
                    >
                      {langContent.key_box_return_instructions || '-'}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AddonPreviewModal: React.FC<AddonPreviewModalProps> = ({
  formData,
  source_language = 'en',
  initialTranslations,
  onTranslationsUpdate,
  open,
  onClose,
  className,
}) => {
  const intl = useIntl();
  const [translateAddon, { isLoading: isTranslating }] =
    useTranslateAddonMutation();

  const [localTranslations, setLocalTranslations] =
    useState<AddonTranslationMap>(() => initialTranslations || {});
  const [translatedContent, setTranslatedContent] =
    useState<AddonTranslationMap>(() => initialTranslations || {});
  const [lastFormData, setLastFormData] = useState('');
  const [contentModified, setContentModified] = useState(false);

  const showKeyBox = addonUsesSecretHandover(formData.access_handover_mode);

  const sourceLang = (
    source_language ||
    formData.language ||
    'en'
  ).toLowerCase();

  const currentPayloadKey = useMemo(
    () =>
      JSON.stringify({
        name: formData.name || '',
        description: formData.description || '',
        access_handover_mode: formData.access_handover_mode || 'NONE',
        pickup: formData.key_box_pickup_instructions || '',
        ret: formData.key_box_return_instructions || '',
        sourceLang,
      }),
    [
      formData.name,
      formData.description,
      formData.access_handover_mode,
      formData.key_box_pickup_instructions,
      formData.key_box_return_instructions,
      sourceLang,
    ]
  );

  useEffect(() => {
    if (open) setContentModified(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (hasTranslations(initialTranslations)) {
      setLocalTranslations(initialTranslations);
      setTranslatedContent(initialTranslations);
      return;
    }

    if (hasTranslations(formData.translations)) {
      setLocalTranslations(formData.translations);
      setTranslatedContent(formData.translations);
      return;
    }

    setLocalTranslations({});
    setTranslatedContent({});
  }, [open, formData.translations, initialTranslations]);

  useEffect(() => {
    if (!lastFormData) {
      setLastFormData(currentPayloadKey);
      setContentModified(false);
      return;
    }
    if (lastFormData !== currentPayloadKey) {
      setContentModified(true);
      setLastFormData(currentPayloadKey);
    }
  }, [currentPayloadKey, lastFormData]);

  const fetchTranslations = useCallback(async () => {
    try {
      const res = await translateAddon({
        name: formData.name || '',
        description: formData.description || '',
        source_language: sourceLang,
        ...(formData.id && !contentModified ? { addon_id: formData.id } : {}),
        access_handover_mode: formData.access_handover_mode,
        key_box_pickup_instructions: showKeyBox
          ? formData.key_box_pickup_instructions
          : undefined,
        key_box_return_instructions: showKeyBox
          ? formData.key_box_return_instructions
          : undefined,
      }).unwrap();

      const responseWithOptionalTopLevelTranslations = res as typeof res & {
        translations?: AddonTranslationMap | null;
      };
      const tr =
        responseWithOptionalTopLevelTranslations.data?.translations ||
        responseWithOptionalTopLevelTranslations.translations;
      if (tr) {
        const normalized = Object.fromEntries(
          Object.entries(tr).map(([k, v]) => [String(k).toLowerCase(), v || {}])
        ) as AddonTranslationMap;
        setTranslatedContent(normalized);
        setLocalTranslations(normalized);
        if (onTranslationsUpdate) onTranslationsUpdate(normalized);
        
        const warningMessage = res?.warning_message;
        if (typeof warningMessage === 'string' && warningMessage.trim()) {
          enqueueSnackbar(warningMessage, { variant: 'warning' });
        } else {
          enqueueSnackbar(
            res?.message || intl.formatMessage({ id: 'ADDON.AUTOTRANSLATE_SUCCESS' }),
            { variant: 'success' }
          );
        }
      }
    } catch (error) {
      enqueueSnackbar(
        getApiErrorMessage(error) ||
          intl.formatMessage({ id: 'ADDON.AUTOTRANSLATE_ERROR' }),
        { variant: 'error' }
      );
    }
  }, [
    translateAddon,
    formData.id,
    formData.name,
    formData.description,
    formData.access_handover_mode,
    formData.key_box_pickup_instructions,
    formData.key_box_return_instructions,
    sourceLang,
    contentModified,
    onTranslationsUpdate,
    intl,
    showKeyBox,
  ]);

  useEffect(() => {
    if (!open) return;

    if (hasTranslations(initialTranslations)) {
      setLocalTranslations(initialTranslations);
      setTranslatedContent(initialTranslations);
      onTranslationsUpdate?.(initialTranslations);
      return;
    }

    if (hasTranslations(formData.translations)) {
      setLocalTranslations(formData.translations);
      setTranslatedContent(formData.translations);
      onTranslationsUpdate?.(formData.translations);
      return;
    }

    if (hasTranslations(localTranslations)) {
      onTranslationsUpdate?.(localTranslations);
      return;
    }

    fetchTranslations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleEditContent = (
    languageCode: string,
    field: keyof AddonTranslationRow,
    value: string
  ) => {
    const lc = String(languageCode).toLowerCase();
    setLocalTranslations((prev) => ({
      ...prev,
      [lc]: {
        ...(prev[lc] || {}),
        [field]: value,
      },
    }));
    setTranslatedContent((prev) => ({
      ...prev,
      [lc]: {
        ...(prev[lc] || {}),
        [field]: value,
      },
    }));
  };

  const handleApplyTranslations = () => {
    if (onTranslationsUpdate) onTranslationsUpdate(localTranslations);
    enqueueSnackbar(intl.formatMessage({ id: 'ADDON.AUTOTRANSLATE_SUCCESS' }), {
      variant: 'success',
    });
    onClose?.({}, 'backdropClick');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx('!flex items-center justify-center', className)}
      aria-labelledby="addon-preview-title"
    >
      <div className="bg-gray-100 w-full max-w-4xl mx-4 rounded-xl shadow-xl flex flex-col overflow-y-auto max-h-[90vh] custom-scrollbar">
        <div className="bg-gray-100 px-6 py-4 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <h1
              id="addon-preview-title"
              className="text-xl font-bold text-gray-800 flex items-center"
            >
              <svg
                className="w-5 h-5 mr-2 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              {intl.formatMessage({ id: 'COMMON.PREVIEW.TITLE' })}
            </h1>
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="text-gray-500 hover:text-gray-700"
            >
              <span className="sr-only">
                {intl.formatMessage({ id: 'COMMON.CLOSE' })}
              </span>
              ✕
            </button>
          </div>
          <p className="text-gray-600 mt-2">
            {intl.formatMessage({ id: 'COMMON.PREVIEW.DESCRIPTION' })}
          </p>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          {I18N_LANGUAGES.filter((l) =>
            ['en', 'fi', 'sv', 'de'].includes(l.code)
          ).map((lang) => (
            <LanguagePreview
              key={lang.code}
              language={lang}
              formData={formData}
              translatedContent={translatedContent}
              isLoading={isTranslating}
              isEditable={!isTranslating}
              showKeyBox={showKeyBox}
              onEditContent={handleEditContent}
            />
          ))}
        </div>

        <div className="bg-gray-100 px-6 py-4 border-t border-gray-100 flex justify-between gap-3">
          <button
            type="button"
            onClick={fetchTranslations}
            className="btn btn-sm bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isTranslating}
            aria-label="Regenerate translations"
          >
            {isTranslating
              ? intl.formatMessage({ id: 'COMMON.GENERATING' })
              : intl.formatMessage({ id: 'COMMON.REGENERATE' })}
          </button>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="btn btn-sm btn-ghost text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg px-4 py-2"
            >
              {intl.formatMessage({ id: 'COMMON.CANCEL' })}
            </button>
            <button
              type="button"
              onClick={handleApplyTranslations}
              className="btn btn-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2"
              disabled={isTranslating}
            >
              {intl.formatMessage({ id: 'COMMON.SAVE' })}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddonPreviewModal;
